import { SpendGate } from '../contracts/spend-gate';

/**
 * The slice of a feature the broadcast pushes into — `AisMapFeature`'s
 * `syncCredits` hook, held structurally so core depends on the contract's
 * SHAPE and never on the feature tier (§4.1: core never depends on features).
 */
export interface SpendSubscriber {
  syncCredits(remaining: number | null, spendingClosed: boolean): void;
}

/**
 * Push price + balance into every controller that prints a cost — extracted
 * verbatim from `VoyageMapController.syncCreditLines` (phase 2), then evolved
 * in phase 5 into what its own doc always promised: THE BROADCAST IS UNIFORM;
 * THE HANDLING IS PER-FEATURE (§4.3). The gate is read exactly once per sync
 * and the same two facts go to every subscriber; each feature then pushes its
 * own controller's setter, because every priced controller has a different
 * signature and that is the honest factoring.
 *
 * A toolbar control has no sensor of its own — `ToolbarControlContext` is
 * `{ controller, slot, options }` — so each controller is TOLD its prices
 * rather than reading them. The balance moves on every spend, and a control
 * quoting a stale one is the bug this mirroring exists to prevent. A future
 * priced feature gets kept in step by implementing `syncCredits`, not by
 * finding all the call sites again.
 *
 * THE BROADCAST IS GENERIC; THE NUMBERS ARE NOT (D1, §6.4). Every figure a
 * subscriber pushes comes from the provider's `SpendGate` — this class never
 * touches a sensor document or a credit catalogue. `spendingClosed` travels
 * with the balance because it is a per-sensor fact a control cannot reach, it
 * decides whether a priced button may be pressed at all, and pushing it on
 * any other schedule would let one drawer offer a spend the next one had
 * already stopped offering. (The nearby feature's storage scope rides its own
 * `syncCredits` for the same reason — invariant I6.)
 *
 * As of phase 6 there is NOTHING hardcoded here: the track bar's line was the
 * last one the facade pushed itself, and it became `TrackFeature.syncCredits`
 * with the rest. Every price line in the app is now a feature's own.
 */
export class SpendBroadcast {
  constructor(
    private readonly gate: SpendGate,
    /**
     * A THUNK, resolved on every `sync()`, never captured — the feature list
     * is the facade's, and construction-time capture would freeze it.
     */
    private readonly subscribers: () => Iterable<SpendSubscriber>,
  ) {}

  sync(): void {
    const remaining = this.gate.remaining();
    const closed = this.gate.spendingClosed();
    for (const s of this.subscribers()) s.syncCredits(remaining, closed);
  }
}
